import { Center, Flex, SimpleGrid, VisuallyHidden } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { Alert, BodyShort } from "@digital-services-gard-as/anchor-react";
import { getExportedInvoices, InvoiceQueryFilter } from "@/api/invoiceApi";
import { getInvoicesQueryOptions } from "@/api/queryOptions/invoiceAPIQueryOptions";
import { useSlowRequestToast } from "@/hooks/useSlowRequestToast";
import { Search } from "@/components/Search";

import { DraftInvoicesTable } from "./Table";
import { ExportTableList } from "../ExportTableList";

const QUERY_LIMIT = 10;
const DEFAULT_QUERY_FILTER: InvoiceQueryFilter = {
    query: "",
    page: 0,
    limit: QUERY_LIMIT,
    sortDirection: "DESC",
    sortBy: "invoiceDate"
};

export const DraftInvoiceTab = () => {
    const { t } = useTranslation(["claimsInvoicesOverview", "errorMessages"]);

    const [queryFilter, setQueryFilter] = useState<InvoiceQueryFilter>({
        ...DEFAULT_QUERY_FILTER,
        includedStatuses: ["Draft"]
    });
    const [searchText, setSearchText] = useState<string>("");
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: DEFAULT_QUERY_FILTER.page,
        pageSize: DEFAULT_QUERY_FILTER.limit
    });
    const [sorting, setSorting] = useState<SortingState>([{ id: "invoiceDate", desc: true }]);

    const {
        data: invoices,
        isFetching: isFetchingInvoices,
        isError: isInvoicesError
    } = useQuery(getInvoicesQueryOptions(queryFilter));

    useSlowRequestToast({
        isLoading: isFetchingInvoices
    });

    /**
     * Triggers invoice API requests whenever sorting state or pagination changes.
     * - Fires on changes to sorting, queryFilter, pageIndex, or pageSize.
     * - Calls getInvoices with the current filter, pagination, and sorting.
     * - Ensures the table updates when user changes sorting, page or rows per page.
     */
    useEffect(() => {
        let { sortBy, sortDirection } = queryFilter;

        if (sorting.length > 0) {
            sortBy = sorting[0].id;
            sortDirection = sorting[0].desc ? "DESC" : "ASC";
        }
        setQueryFilter({
            ...queryFilter,
            sortBy,
            sortDirection,
            limit: pageSize,
            page: pageIndex
        });
    }, [pageIndex, pageSize, sorting]);

    /**
     * Resets pagination to the initial state.
     */
    const resetPagination = useCallback(() => {
        setPagination({ pageSize: DEFAULT_QUERY_FILTER.limit, pageIndex: 0 });
    }, [setPagination]);

    /**
     * Reset pagination and fetch invoices on Enter key press.
     */
    const handleKeyUp = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                resetPagination();
                if (queryFilter.query !== searchText) {
                    setQueryFilter({ ...queryFilter, query: searchText });
                }
            }
        },
        [queryFilter, resetPagination, searchText]
    );
    /**
     * Updates the query filter when the search text changes.
     */
    const handleSearchTextChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(e.target.value);
        },
        [setSearchText]
    );
    /**
     * Triggers invoice API requests when the search button is clicked.
     */
    const handleSearchClick = useCallback(() => {
        resetPagination();
        if (queryFilter.query !== searchText) {
            setQueryFilter({ ...queryFilter, query: searchText });
        }
    }, [resetPagination, queryFilter, searchText]);
    /**
     * Triggers invoice API requests when the clear search button is clicked.
     */
    const handleClearSearch = useCallback(() => {
        resetPagination();
        setSearchText("");
        setQueryFilter({ ...queryFilter, query: "" });
    }, [queryFilter, resetPagination]);

    const handleExportInvoices = useCallback(async (invoiceIds?: string[], exportQueryFilter?: InvoiceQueryFilter) => {
        await getExportedInvoices(invoiceIds, exportQueryFilter);
    }, []);
    /**
     * Triggers invoice API requests when the export button is clicked.
     */

    return (
        <Flex mt="1.5rem" gap="2rem" flexDir="column" w="100%">
            {isInvoicesError && (
                <Center>
                    <Alert id="draftInvoicesNoDataAlert" variant="danger" data-testid="draftInvoicesNoDataAlert">
                        <BodyShort>{t("errorMessages:claimsInvoicesOverview.cannotRetrieveData")}</BodyShort>
                    </Alert>
                </Center>
            )}
            {/* eslint-disable-next-line no-magic-numbers */}
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={2} w="100%">
                <Flex justifyContent="start">
                    <Search
                        id="draftInvoices"
                        placeholder={t("claimsInvoicesOverview:invoiceSearchPlaceholder")}
                        queryFilter={queryFilter}
                        searchText={searchText}
                        isFetchingData={isFetchingInvoices}
                        handleSearchCriteriaChange={handleSearchTextChange}
                        handleKeyUp={handleKeyUp}
                        handleSearchClick={handleSearchClick}
                        handleClearSearch={handleClearSearch}
                    />
                </Flex>
                <Flex justifyContent="end" gap="1rem" mr="1rem" alignContent="end">
                    {invoices && invoices?.items?.length > 0 ? (
                        <ExportTableList
                            invoiceIds={invoices.items.map(invoice => invoice.id)}
                            queryFilter={queryFilter}
                            id="draftInvoices"
                            handleExport={handleExportInvoices}
                        />
                    ) : (
                        <VisuallyHidden />
                    )}
                </Flex>
            </SimpleGrid>

            <DraftInvoicesTable
                invoicesResult={invoices}
                pageIndex={pageIndex}
                pageSize={pageSize}
                setPagination={setPagination}
                sorting={sorting}
                setSorting={setSorting}
                isFetchingInvoices={isFetchingInvoices}
            />
        </Flex>
    );
};
